import "./style.scss";

import type { TAdvancePayment } from "components/AdvanceManagement/NewAdvanceModal";
import { formatInitialSlices } from "components/AdvanceManagement/NewAdvanceModal/utils";
import NewForm from "components/NewForm";
import { handleScrollToErrors } from "components/NewForm/utils";
import ModalBase from "components/shared/ModalBase";
import RepresentativeForm from "components/shared/Representatives/RepresentativeForm";
import type { IBeneficiar, IRepresentative } from "containers/Beneficiars/types";
import CreateChannelModal from "containers/Channels/CreateChannelModal";
import type { IChannel } from "containers/Channels/types";
import type { TFormCustomField } from "components/TitleFormContainer/types";
import type { ITitle } from "containers/Titles/types";
import { termFieldsForMode } from "containers/CustomFieldsContainer/customFieldPayload";
import { formatCustomFields, formatFieldsForDefaultValues } from "containers/CustomFieldsContainer/helpers";
import type { IWork, IWorkTitleSummary } from "containers/Works/types";
import { coverUrlForWork, workPreviewFromTerm } from "containers/Works/workPreview";
import { isBookPublisher as isBookPublisherCompany } from "helpers/publisher";
import { getCleanStartorEndDay, getCurrentPeriod, isObjectEmpty } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
    Control,
    FieldErrors,
    UseFormClearErrors,
    UseFormSetError,
    UseFormSetValue,
    UseFormTrigger,
    UseFormWatch,
} from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { DTOCreateAdvancePaymentSlice, IAdvanceSlice } from "store/advancePaymentSlice";
import { addAdvancePayment, deleteAdvancePayment, updateAdvancePayment } from "store/advancePaymentSlice";
import type { TermGroup } from "store/beneficiaryStore";
import {
    addCreatedTerm as addCreatedBeneficiaryTerm,
    fetchAssociatedPersons,
    fetchBeneficiar,
    fetchTermAssociatedToBeneficiaries,
    fetchTermGroups,
} from "store/beneficiaryStore";
import type { CalculationBaseColumn } from "store/calculationBaseColumnLabelsStore";
import type { ChannelCompensationGroup } from "store/channelStore";
import { fetchActivetedChannels, fetchChannelCompensationGroups } from "store/channelStore";
import { fetchCustomFields } from "store/customFieldsStore";
import { buildCreatedTermListEntry } from "store/createdTerm";
import type { ITerm } from "store/managementStore";
import { fetchBeneficiariesInformation } from "store/managementStore";
import type { TPeriod } from "store/periodStore";
import { fetchPeriods } from "store/periodStore";
import { useAppSelector } from "store/store";
import { useAppDispatch } from "store/store";
import { createTerm, editTerm } from "store/termStore";
import { fetchAdvancePaymentsOfTerm, fetchTerm } from "store/termStore";
import { fetchAllTermTemplates, fetchBeneficiaryFrameworkContracts } from "store/termTemplatesStore";
import { fetchTitleFormatTypes } from "store/titleFormatTypeStore";
import {
    addCreatedTerm as addCreatedTitleTerm,
    fetchTermAssociatedToTitle,
    fetchTitle,
    fetchTitleSales,
    resetTermAssociatedToTitles,
} from "store/titleStore";
import type { TFetchStatus, TFilterParamsPaymentStatus } from "store/types";
import { PaymentStatus } from "store/types";
import { addCreatedTerm as addCreatedWorkTerm, fetchWork, fetchWorkTerms } from "store/workStore";
import type { Binary } from "types";
import { v4 as uuidv4 } from "uuid";

import ChannelsStep from "./ChannelsStep";
import { resolveCalculationBaseColumn } from "./ChannelsStep/calculationBaseColumnUtils";
import { resolveMilestoneBaseColumn } from "./ChannelsStep/milestoneBaseColumnUtils";
import { normalizeCoveredTitleIds, termCoveragesConflict } from "./coveredTitles";
import NewTermStep from "./NewTermStep";
import RecapStep from "./RecapStep";
import SettingsStep from "./SettingsStep";
import {
    areCompensationGroupFormulasSynced,
    checkElementTerms,
    formatChannelsForSaving,
    getDefaultIsTitlePublishedBeforePeriod,
    splitDefaultConfigurationFlag,
    getEarliestWorkReleaseDate,
} from "./utils";

export interface GroupParams extends ChannelCompensationGroup {
    data: {
        percentage: number | string;
        start: number | string;
        quantity: number | string;
    }[];
    channels?: IChannel[] | ChannelParams[];
    synced?: boolean;
}

export interface ChannelData {
    percentage: number | string;
    start: number | string;
    quantity: number | string;
    /**
     * Display marker stored on a hidden 0% default so a later edit of the contract
     * keeps that formula off the channel step. Not a royalty input.
     */
    showDefaultConfiguration?: boolean;
}

export interface ChannelParams {
    channelId: number;
    data: ChannelData[] | string;
    calculationBaseColumn?: CalculationBaseColumn;
    milestoneBaseColumn?: CalculationBaseColumn;
    channelKind?: number;
    channelName?: string;
    groupId?: number;
    formatTypeId?: number | null;
    /**
     * Typology only. False hides this channel's default formula on the contract form
     * and persists that formula at 0%. Absent means show it.
     */
    showDefaultConfiguration?: boolean;
}

export interface ITermFormValues {
    title: ITitle | null;
    work: IWork | null;
    beneficiary: IBeneficiar | null;
    representative: IRepresentative | null;
    channels: { channelId: number; data: string }[];
    initialAdvance: string | number;
    advancePayment: string | number;
    reference: string | undefined;
    /**
     * Kind of contract (AUT, TRAD, ILLU…), exported as Cegid GECT contract class
     */
    contractTypology?: string | null;
    endDate: Date | null | undefined | string;
    initialProvision: string | number;
    compensate: number | undefined;
    titleId: number | null | undefined;
    workId?: number | null;
    beneficiarId: number | null | undefined;
    representativeId: number | null | undefined;
    advanceCompensationGroup: TermGroup | null;
    /**
     * It is actually the period matching the periodTypeId chosen.
     */
    periodType: TPeriod | null;
    cumulativeRights: string | number;
    advancePaymentId: number | null | undefined;
    slices: DTOCreateAdvancePaymentSlice[];
    /** Title release date (from title.releaseDate). */
    titleReleaseDate: string | Date | null;
    /** 1 = Yes published before (hide creation data), 0 = No (show creation data). */
    isTitlePublishedBeforePeriod: Binary;
    coveredTitles: IWorkTitleSummary[];
    customFields?: TFormCustomField[];
}

export interface ITermFormData {
    errors: FieldErrors<ITermFormValues>;
    watch: UseFormWatch<ITermFormValues>;
    control: Control<ITermFormValues>;
    dirtyFields: any;
    setError?: UseFormSetError<ITermFormValues>;
    setValue?: UseFormSetValue<ITermFormValues>;
    clearErrors?: UseFormClearErrors<ITermFormValues>;
}

export interface IFormStep {
    stepTitle: string;
    stepSubtitle: string;
    conditionToDisable?: boolean;
    /** Forward navigation stays on this step and runs this callback. Next stays enabled. */
    onBlockForward?: () => void;
    format: (stepTitle: string, stepSubtitle: string) => JSX.Element;
}
interface TermFormContainerProps {
    /**
     * Whether it is a form for creation or edits.
     */
    mode: "new" | "edit";
    /**
     * (only for edits) Data of term that is being edited.
     */
    currentTerm?: ITerm;
    /**
     * (only for edits) chosen groups with data
     */
    groups?: GroupParams[];
    /**
     * (only for edits) Triggers closing of edit form.
     */
    onEdit?: () => void;
    /**
     * Whether we're reaching the form from a title or a beneficiary
     */
    from?: "beneficiars" | "titles" | "works";
    /**
     * Triggers parent function for deleting term.
     */
    onDelete?: (termId: number) => void;
    /**
     * Triggers parent function when isDirty changes.
     */
    onDirty?: (isDirty: boolean) => void;
    /**
     * Advance Payment linked on term creation. Needed on edit.
     */
    linkedAdvancePayment?: IAdvanceSlice;
}

export interface ITermMessagesOptions {
    payment: boolean;
    benefPeriods: boolean;
    titlePeriods: ITerm[];
    termExists: boolean;
}
/**
 * A container that handles the form for a new term or for editing one. It contains all data needed for the form and handles dispatches.
 */
const TermFormContainer = (props: TermFormContainerProps) => {
    const { mode, currentTerm, onEdit, from, onDelete, groups, onDirty = () => {}, linkedAdvancePayment } = props;
    const [t] = useTranslation();
    const location = useLocation();
    const { id: routeResourceId } = useParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [activatedChannels, setActivatedChannels] = useState<number[]>([]);
    const [chosenChannels, setChosenChannels] = useState<ChannelParams[]>([]);
    const [activeGroups, setActiveGroups] = useState<number[]>([]);
    const [chosenGroups, setChosenGroups] = useState<GroupParams[]>([]);
    const [submitFormStatus, setSubmitFormStatus] = useState<TFetchStatus>("idle");
    const [usesTemplate, setUsesTemplate] = useState(false);
    const [isEditChannelsReady, setIsEditChannelsReady] = useState(mode !== "edit");

    const { activePeriod, periods } = useAppSelector((state) => state.period);
    const initialPeriod = periods.find((item) => item.id === activePeriod);
    const [currentPeriod, setCurrentPeriod] = useState<TPeriod | undefined>(initialPeriod);

    // For the radio button form in SettingsStep:
    const [termHasAdvanceGroups, setTermHasAdvanceGroups] = useState(false);

    // To know if the radio btn for the Advance Groups has changed during edit:
    const [hasAdvanceGroupsOptionChanged, setHasAdvanceGroupsOptionChanged] = useState(false);

    // To know if any channels or values within the channels have changed:
    const [haveChannelValuesChanged, setHaveChannelValuesChanged] = useState(false);

    // To be able to create the same advance in the Advance Management page.
    // null  = "Non" sélectionné (ou initial) → pas d'à-valoir, pas de blocage
    // false = "Oui" sélectionné mais montants vides → bloque le bouton Suivant
    // true  = "Oui" avec montants renseignés → à-valoir à créer
    const [createAdvanceInManagement, setCreateAdvanceInManagement] = useState<boolean | null>(
        mode === "edit" && linkedAdvancePayment ? true : null,
    );
    const [advancePayment, setAdvancePayment] = useState<TAdvancePayment | null>(null);
    const [paymentExistsInManagement, setPaymentExistsInManagement] = useState(false);

    const [navigateData, setNavigateData] = useState({
        path: "",
        state: { tab: "", termId: null, advancePaymentId: null },
    });

    const { getCompanySettings } = useCompanySettings();
    const usesChannelCompensation = getCompanySettings("uses_interchannel_compensation");
    const usesAdvanceCompensation = getCompanySettings("uses_interterm_compensation");
    const usesMultiPeriods = getCompanySettings("uses_simultaneous_period");
    const usesInitialAdvance = getCompanySettings("uses_initial_advance");
    const usesCumulativeRoyalties = getCompanySettings("uses_cumulative_royalties");

    const { title, termAssociatedToTitle, termAssociatedToTitleStatus } = useAppSelector((state) => state.titles);
    const { selectedWork, workTerms, workTermsStatus } = useAppSelector((state) => state.works);
    const { items: titleFormatTypes, status: titleFormatTypesStatus } = useAppSelector(
        (state) => state.titleFormatTypes,
    );
    const { beneficiarsAutocomplete, associatedPersons, beneficiar, termGroups, termAssociatedToBeneficiaries } =
        useAppSelector((state) => state.beneficiary);
    const { channels, activedChannels, channelCompensationGroups } = useAppSelector((state) => state.channels);
    const { termTemplates, beneficiaryFrameworkContracts } = useAppSelector((state) => state.termTemplates);
    const { advanceSlices } = useAppSelector((state) => state.advancePayment);

    // To display warning messages about recomputation or title/beneficiary on other period:
    const [otherTermsMessage, setOtherTermsMessage] = useState<ITermMessagesOptions>({
        payment: false,
        benefPeriods: false,
        titlePeriods: [] as ITerm[],
        termExists: false,
    });

    const { companyInfos } = useCompanySettings();
    const isBookPublisher = isBookPublisherCompany(companyInfos?.publisherType);

    // Mainly for edit mode: returns array with channels that don't exist within a group
    const filteredChannels = useMemo(() => {
        const groupIds = channelCompensationGroups.map((group) => group.id);
        return usesChannelCompensation
            ? channels.filter((channel) => !groupIds.includes(channel?.compensationGroupId ?? -2))
            : channels;
    }, [channels, channelCompensationGroups]);

    // Mainly for edit mode: returns array with channels that exist within a group
    const groupsWithChannels = useMemo(() => {
        const allGroups = channelCompensationGroups.map((group) => {
            const connectedChannels = channels.filter((channel) => channel.compensationGroupId === group.id);
            return mode === "new"
                ? { ...group, channels: connectedChannels, synced: true }
                : { ...group, channels: connectedChannels };
        });

        return allGroups.filter((group) => group.channels.length > 0);
    }, [channelCompensationGroups, channels]);

    const currentTitleId =
        mode === "edit"
            ? currentTerm?.titleId
            : (location.state?.titleId as number | undefined) ??
              (from === "titles" && routeResourceId ? Number(routeResourceId) : undefined);
    const currentWorkId =
        mode === "edit"
            ? currentTerm?.workId ?? undefined
            : (location.state?.workId as number | undefined) ??
              (from === "works" && routeResourceId ? Number(routeResourceId) : undefined);
    const workFromNavigation =
        location.state?.work != null && (location.state.work as IWork).id === currentWorkId
            ? (location.state.work as IWork)
            : null;
    const currentBeneficiaryId = mode === "edit" ? currentTerm?.beneficiarId : location.state?.beneficiaryId;
    const chosenRepresentative =
        mode === "edit" ? associatedPersons?.find((person) => person.id === currentTerm?.representativeId) : null;

    const [addRepresentative, setAddRepresentative] = useState(false);
    const [addChannel, setAddChannel] = useState(false);

    const defaultValues: ITermFormValues = useMemo(() => {
        const prefilledTitle = currentTitleId === title?.id ? title : null;
        const knownWork =
            selectedWork?.id === currentWorkId
                ? selectedWork
                : workFromNavigation?.id === currentWorkId
                  ? workFromNavigation
                  : null;
        const prefilledWork = workPreviewFromTerm(
            currentWorkId != null
                ? {
                      workId: currentWorkId,
                      title: currentTerm?.title ?? knownWork?.name ?? "",
                      titleAvatarUrl: currentTerm?.titleAvatarUrl,
                      avatarUrl: currentTerm?.avatarUrl,
                  }
                : currentTerm,
            knownWork,
        );
        const titleReleaseDate = prefilledTitle?.releaseDate ?? getEarliestWorkReleaseDate(prefilledWork) ?? null;
        return {
            title: prefilledTitle,
            work: prefilledWork,
            beneficiary:
                currentBeneficiaryId === beneficiar?.id && Object.entries(beneficiar).length > 0 ? beneficiar : null,
            representative: null as IRepresentative | null,
            channels: [],
            initialAdvance: mode === "edit" ? ((currentTerm?.initialAdvance ?? 0) / 100).toString() : "",
            advancePayment: mode === "edit" ? ((currentTerm?.advancePayment ?? 0) / 100).toString() : "",
            reference: mode === "edit" ? currentTerm?.reference : "",
            contractTypology: mode === "edit" ? currentTerm?.contractTypology ?? "" : "",
            // Rebuild a local Date from the UTC calendar parts so the picker shows the
            // day the backend stored, not the previous day when the user sits west of UTC.
            endDate: mode === "edit" && currentTerm?.endDate ? getCleanStartorEndDay(currentTerm.endDate) : null,
            initialProvision: mode === "edit" ? ((currentTerm?.initialProvision ?? 0) / 100).toString() : "",
            compensate: mode === "edit" ? currentTerm?.compensate : +(companyInfos?.useCompensationByDefault ?? 1),
            titleId: mode === "edit" ? currentTerm?.titleId : title?.id ?? null,
            beneficiarId: mode === "edit" ? currentTerm?.beneficiarId : beneficiar?.id ?? null,
            representativeId: mode === "edit" ? currentTerm?.representativeId : null,
            advanceCompensationGroup: mode === "edit" && usesAdvanceCompensation ? ({} as TermGroup) : null,
            periodType: null,
            cumulativeRights: mode === "edit" ? ((currentTerm?.cumulativeRights ?? 0) / 100).toString() : "",
            advancePaymentId: mode === "edit" ? currentTerm?.advancePaymentId : null,
            slices: [
                {
                    amount: 0,
                    paymentDate: null,
                    status: PaymentStatus.NOT_PAID,
                    comment: "",
                    key: uuidv4(),
                },
            ],
            titleReleaseDate,
            isTitlePublishedBeforePeriod:
                mode === "edit"
                    ? currentTerm?.isTitlePublishedBeforePeriod ?? 0
                    : getDefaultIsTitlePublishedBeforePeriod(titleReleaseDate, initialPeriod),
            coveredTitles: [],
            customFields: [],
        };
    }, [
        termGroups,
        currentTerm,
        title,
        selectedWork,
        workFromNavigation,
        initialPeriod,
        mode,
        currentTitleId,
        currentWorkId,
    ]);
    const formMethods = useForm<ITermFormValues>({
        defaultValues,
        mode: "all",
    });
    const {
        control,
        handleSubmit,
        setValue,
        watch,
        getValues,
        trigger,
        formState: { errors, isDirty, dirtyFields },
        setError,
        clearErrors,
    } = formMethods;
    const { customFields: storedCustomFields, customFieldsStatus } = useAppSelector((state) => state.customFields);
    const visibleTermFields = useMemo(() => termFieldsForMode(storedCustomFields, mode), [storedCustomFields, mode]);

    useEffect(() => {
        if (customFieldsStatus === "idle") dispatch(fetchCustomFields());
    }, [customFieldsStatus, dispatch]);

    useEffect(() => {
        if (customFieldsStatus !== "succeeded") return;
        setValue(
            "customFields",
            formatFieldsForDefaultValues({
                customFields: visibleTermFields,
                editModeActive: mode === "edit",
                typeData: currentTerm ?? null,
            }),
        );
    }, [customFieldsStatus, visibleTermFields, mode, currentTerm, setValue]);

    const chosenBeneficiary = watch("beneficiary");
    const chosenTitle = watch("title");
    const chosenWork = watch("work");
    const chosenCoveredTitles = watch("coveredTitles");
    const loadedWorkIdRef = useRef<number | null>(null);
    const [loadedWorkId, setLoadedWorkId] = useState<number | null>(null);
    const requestedWorkId = isBookPublisher ? currentWorkId ?? chosenWork?.id ?? null : null;
    const workTitlesLoading = requestedWorkId != null && loadedWorkId !== requestedWorkId;
    const workTitlesForCoverage = isBookPublisher && !workTitlesLoading ? chosenWork?.titles ?? [] : [];
    const coveredTitlesHydratedWorkIdRef = useRef<number | null>(null);
    const companyFormatTypes = useMemo(
        () => titleFormatTypes.map((formatType) => ({ id: formatType.id, name: formatType.name })),
        [titleFormatTypes],
    );
    const chosenAdvance = watch("advancePayment");
    const chosenCompensation = watch("compensate");
    const chosenAdvanceGroup = watch("advanceCompensationGroup");
    const chosenInitialAdvance = +watch("initialAdvance");
    const chosenCumulativeRights = watch("cumulativeRights");
    const chosenPeriodType = watch("periodType");
    const chosenProvision = watch("initialProvision");

    const checkIfGroupsAreCanceled = () => {
        // If they said they use compensation groups, see if they chose a group or not.
        if (termHasAdvanceGroups) {
            return chosenAdvanceGroup?.id ?? null;
        } else {
            // If they went from yes to no, change group to null (to delete it)
            return null;
        }
    };

    const onSubmit = (data: ITermFormValues) => {
        setSubmitFormStatus("pending");
        // Need to stringify data in order to save it
        const channelsData = formatChannelsForSaving(chosenChannels, channels);

        const periodTypeId = data.periodType?.periodTypeId;

        const {
            titleReleaseDate: _titleReleaseDate,
            coveredTitles,
            customFields: formCustomFields,
            ...formData
        } = data;

        const resolvedWorkId = chosenWork?.id ?? currentWorkId ?? chosenTitle?.workId ?? null;
        const resolvedTitleId = isBookPublisher ? null : chosenTitle?.id ?? null;
        const afterCreatePath = currentBeneficiaryId
            ? `/beneficiars/${chosenBeneficiary?.id}`
            : resolvedWorkId
              ? `/works/${resolvedWorkId}`
              : chosenTitle?.id
                ? `/titles/${chosenTitle.id}`
                : "/works";

        const termData = {
            ...formData,
            channels: channelsData,
            titleId: resolvedTitleId ?? undefined,
            workId: resolvedWorkId,
            beneficiarId: chosenBeneficiary && chosenBeneficiary?.id,
            representativeId: currentRepresentative?.id ?? null,
            initialProvision: Math.round((+data.initialProvision.toString().replace(",", ".") || 0) * 100),
            initialAdvance: Math.round((+data.initialAdvance.toString().replace(",", ".") || 0) * 100),
            advancePayment: Math.round((+data.advancePayment.toString().replace(",", ".") || 0) * 100),
            cumulativeRights: Math.round((+data.cumulativeRights.toString().replace(",", ".") || 0) * 100),
            compensationGroupId: mode === "edit" ? checkIfGroupsAreCanceled() : chosenAdvanceGroup?.id ?? null,
            // Always format from the current form value: default is a local Date rebuilt
            // via getCleanStartorEndDay, which would JSON.stringify to a shifted UTC ISO
            // otherwise. toLocaleDateString("en-CA") returns the local calendar day the
            // user sees in the picker, matching the intended UTC calendar day.
            endDate: data?.endDate ? (data.endDate as Date).toLocaleDateString("en-CA") : null,
            periodTypeId,
            periodId: mode === "new" ? (usesMultiPeriods ? data.periodType?.id : activePeriod) : undefined,
            isTitlePublishedBeforePeriod: Number(data.isTitlePublishedBeforePeriod),
            customFields: formatCustomFields(formCustomFields, visibleTermFields),
            coveredTitleIds: normalizeCoveredTitleIds(
                (coveredTitles ?? []).map((title) => title.id),
                workTitlesForCoverage.map((title) => title.id),
            ),
        };

        const hasLinkedResource = isBookPublisher ? !!chosenWork || !!resolvedWorkId : !!chosenTitle;
        if (mode === "new" && chosenBeneficiary && hasLinkedResource) {
            dispatch(createTerm({ data: termData as unknown as ITermFormValues })).then((resp) => {
                if (resp.meta.requestStatus !== "fulfilled") {
                    setSubmitFormStatus("failed");
                    return;
                }
                const termId = resp.payload?.data?.result;
                const createdTermId = Number(termId);
                if (Number.isFinite(createdTermId)) {
                    const representative =
                        data.representative != null && data.representative.id > 0 ? data.representative : null;
                    const workForDisplay = chosenWork ?? (selectedWork?.id === resolvedWorkId ? selectedWork : null);
                    const releaseDate = isBookPublisher ? null : chosenTitle?.releaseDate;
                    const entry = buildCreatedTermListEntry({
                        id: createdTermId,
                        titleId: resolvedTitleId,
                        workId: resolvedWorkId,
                        beneficiarId: chosenBeneficiary.id,
                        title: isBookPublisher ? workForDisplay?.name ?? "" : chosenTitle?.name ?? "",
                        authorSurname: chosenBeneficiary.surname,
                        authorName: chosenBeneficiary.name,
                        beneficiaryAvatarUrl: chosenBeneficiary.avatarUrl ?? null,
                        resourceAvatarUrl: isBookPublisher
                            ? coverUrlForWork(workForDisplay) ?? null
                            : chosenTitle?.avatarUrl ?? null,
                        representativeSurname: representative?.surname,
                        representativeName: representative?.name,
                        representativeId: representative?.id ?? null,
                        computeFromRepresentative: representative?.computeFromRepresentative ?? false,
                        endDate: typeof termData.endDate === "string" ? termData.endDate : null,
                        reference: termData.reference ?? null,
                        periodTypeId: periodTypeId ?? null,
                        compensationGroupId: termData.compensationGroupId ?? null,
                        compensate: Number(termData.compensate) || 0,
                        isbn: isBookPublisher ? null : chosenTitle?.ean13 ?? null,
                        releaseDate:
                            releaseDate == null
                                ? null
                                : releaseDate instanceof Date
                                  ? releaseDate.toISOString()
                                  : String(releaseDate),
                        isTitlePublishedBeforePeriod: Number(termData.isTitlePublishedBeforePeriod) === 1 ? 1 : 0,
                        coveredTitleIds: termData.coveredTitleIds ?? [],
                        originalPeriod: Number(termData.periodId) || 0,
                    });
                    dispatch(addCreatedWorkTerm(entry));
                    dispatch(addCreatedTitleTerm(entry));
                    dispatch(addCreatedBeneficiaryTerm(entry));
                }
                setSubmitFormStatus("succeeded");

                if (createAdvanceInManagement && advancePayment) {
                    dispatch(
                        addAdvancePayment({
                            termId: termId,
                            slices: advancePayment.slices,
                            isForfait: 0,
                            periodId: currentPeriod?.id as number,
                            forceOver4Pass: 0,
                        }),
                    ).then((resp) => {
                        if (resp.meta.requestStatus === "fulfilled") {
                            termData.advancePaymentId = resp.payload.data.id;
                            dispatch(editTerm({ data: termData as unknown as ITermFormValues, id: termId }));
                            setNavigateData({
                                path: afterCreatePath,
                                state: {
                                    tab: "2",
                                    termId: termId,
                                    advancePaymentId: resp.payload.data.id,
                                },
                            });
                        }
                    });
                }
                if (!createAdvanceInManagement) {
                    navigate(afterCreatePath, {
                        state: { tab: "2", termId: termId },
                    });
                }
            });
        }

        if (mode === "edit") {
            dispatch(
                editTerm({ id: currentTerm?.termId?.toString() ?? "", data: termData as unknown as ITermFormValues }),
            ).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    dispatch(fetchTerm(parseInt(resp.meta.arg.id))).then(() => {
                        onEdit && onEdit();
                        setHaveChannelValuesChanged(false);
                        setHasAdvanceGroupsOptionChanged(false);
                        setSubmitFormStatus("succeeded");

                        // Always refetch the beneficiary's terms list so the beneficiary page
                        // reflects edits (endDate, reference, provisions...) — not just group changes.
                        if (currentBeneficiaryId) {
                            dispatch(fetchTermAssociatedToBeneficiaries({ beneficiaryId: currentBeneficiaryId }));
                        }
                    });

                    if (createAdvanceInManagement && advancePayment) {
                        if (currentTerm?.termId && currentPeriod) {
                            if (linkedAdvancePayment) {
                                dispatch(
                                    updateAdvancePayment({
                                        data: {
                                            slices: advancePayment.slices,
                                            periodId: currentPeriod?.id,
                                            isForfait: 0,
                                            forceOver4Pass: 0,
                                        },
                                        termId: currentTerm.termId,
                                    }),
                                );
                            } else {
                                dispatch(
                                    addAdvancePayment({
                                        termId: currentTerm?.termId,
                                        slices: advancePayment.slices,
                                        isForfait: 0,
                                        periodId: currentPeriod?.id as number,
                                        forceOver4Pass: 0,
                                    }),
                                );
                            }
                        }
                    } else if (!isObjectEmpty(linkedAdvancePayment) && !createAdvanceInManagement) {
                        // If they'd created a payment and now decide to delete it (only possible if period still open)
                        // we delete only the payment without updating the tpd as the term may still have a balance.
                        dispatch(deleteAdvancePayment({ id: linkedAdvancePayment!.id, connectedTPD: -1 }));
                    }
                } else if (resp.meta.requestStatus === "rejected") {
                    if (resp.payload?.message === "ServerError.InvalidChannelConfiguration") {
                        const fieldEl = document.querySelector("#step-1"); // querySelector less code heavy
                        if (fieldEl) {
                            fieldEl.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                    }
                    setSubmitFormStatus("failed");
                }
            });
        }
    };

    // Handle navigate when creating contract with payment, it doesn't work
    useEffect(() => {
        if (!navigateData.path) return;
        const { path, state } = navigateData;
        navigate(path, { state });
    }, [navigateData]);

    const currentRepresentative = watch("representative");

    useEffect(() => {
        if (currentRepresentative?.id == -1) {
            setAddRepresentative(true);
            setValue("representative", null);
        }
    }, [currentRepresentative]);

    const handleCreateRepresentative = async (newRepresentative: IRepresentative) => {
        await setValue("representative", newRepresentative);
        setAddRepresentative(false);
    };

    useEffect(() => {
        // If no beneficiary is selected, delete the value set on representee
        if (!chosenBeneficiary) setValue("representative", null);
        // (Only on edit) If a beneficiary is chosen and they have a representee, set value to it.
        if (chosenBeneficiary && chosenRepresentative && mode === "edit") {
            setValue("representative", chosenRepresentative);
        }
    }, [chosenBeneficiary, chosenRepresentative]);

    useEffect(() => {
        // When beneficiary chosen, fetch the representees.
        // Needed to separate this useffect with one above to avoir infinite renderings.
        if (chosenBeneficiary) {
            setValue("representative", null);
            dispatch(fetchAssociatedPersons(chosenBeneficiary?.id));
        }
    }, [chosenBeneficiary]);

    useEffect(() => {
        if (!isBookPublisher || titleFormatTypesStatus !== "idle") return;
        dispatch(fetchTitleFormatTypes());
    }, [dispatch, isBookPublisher, titleFormatTypesStatus]);

    useEffect(() => {
        if (!isBookPublisher) return;
        const workId = currentWorkId ?? chosenWork?.id;
        if (!workId) {
            loadedWorkIdRef.current = null;
            setLoadedWorkId(null);
            return;
        }
        if (loadedWorkIdRef.current === workId) return;
        loadedWorkIdRef.current = workId;
        dispatch(fetchWork(workId)).then((resp) => {
            if (loadedWorkIdRef.current !== workId) return;
            if (!fetchWork.fulfilled.match(resp)) {
                loadedWorkIdRef.current = null;
                return;
            }
            setLoadedWorkId(workId);
            setValue("work", resp.payload);
        });
    }, [chosenWork?.id, currentWorkId, dispatch, isBookPublisher, setValue]);

    useEffect(() => {
        if (isBookPublisher) return;
        const createFromTitleButNoData = !title && from === "titles";
        const editModeFromBeneficiary = mode === "edit" && from === "beneficiars" && !chosenTitle;
        if ((createFromTitleButNoData || editModeFromBeneficiary) && currentTitleId) {
            dispatch(fetchTitle(currentTitleId)).then((resp) => {
                if (resp.meta.requestStatus === "rejected") return;
                setValue("title", resp.payload);
            });
        }
    }, [chosenTitle, currentTitleId, from, isBookPublisher, title]);

    useEffect(() => {
        const createFromBeneficiaryButNoData = !Object.keys(beneficiar).length && from === "beneficiars";
        const editModeFromFromTitle = mode === "edit" && from === "titles" && !chosenBeneficiary;
        // (Only on edit) If the beneficiary value isn't filled from store, fetch again.
        if (createFromBeneficiaryButNoData || editModeFromFromTitle) {
            dispatch(fetchBeneficiar(currentBeneficiaryId)).then((resp) => {
                if (resp.meta.requestStatus === "rejected") return;
                setValue("beneficiary", resp.payload);
            });
        }
    }, [currentBeneficiaryId, chosenBeneficiary]);

    useEffect(() => {
        // Fetch channels of the company.
        if (usesChannelCompensation && !channelCompensationGroups.length) {
            dispatch(fetchChannelCompensationGroups());
        }
    }, [usesChannelCompensation, channelCompensationGroups]);

    const workReleaseDate = isBookPublisher ? getEarliestWorkReleaseDate(chosenWork) : null;
    const workFirstTitleId = chosenWork?.titles?.[0]?.id;

    useEffect(() => {
        if (isBookPublisher && chosenWork && mode === "new") {
            setValue("titleReleaseDate", workReleaseDate);
            setValue(
                "isTitlePublishedBeforePeriod",
                getDefaultIsTitlePublishedBeforePeriod(workReleaseDate, initialPeriod),
            );
        }
    }, [chosenWork?.id, workReleaseDate, isBookPublisher, initialPeriod, mode, setValue]);

    useEffect(() => {
        if (isBookPublisher) {
            if (!chosenWork?.id) return;
            if (workFirstTitleId != null) dispatch(fetchActivetedChannels(workFirstTitleId));
            dispatch(fetchWorkTerms(chosenWork.id));
            return;
        }
        if (!chosenTitle) return;
        dispatch(fetchActivetedChannels(currentTitleId ?? chosenTitle.id));
        dispatch(fetchTermAssociatedToTitle({ titleId: chosenTitle.id }));
    }, [currentTitleId, chosenTitle, chosenWork?.id, workFirstTitleId, isBookPublisher]);

    // Fetch periods only if active period undefined (like when refreshing)
    useEffect(() => {
        if (!activePeriod) {
            dispatch(fetchPeriods()).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setCurrentPeriod(getCurrentPeriod(resp.payload));
                }
            });
        }

        if (chosenPeriodType) {
            setCurrentPeriod(chosenPeriodType);
        }
    }, [activePeriod, chosenPeriodType]);

    useEffect(() => {
        if (chosenBeneficiary && usesAdvanceCompensation) {
            dispatch(fetchTermGroups({ beneficiaryId: chosenBeneficiary.id })).then((resp) => {
                // Needed to update advanceCompensationGroup value, otherwise it stays on default (which is No)
                if (mode === "edit" && resp.meta.requestStatus === "fulfilled") {
                    const group = resp.payload.find(
                        (group: TermGroup) => group.id === currentTerm?.compensationGroupId,
                    );
                    setValue("advanceCompensationGroup", group);
                }
            });
        }
    }, [chosenBeneficiary]);

    useEffect(() => {
        // Needed to display warnings on creation of new term
        if (chosenBeneficiary) {
            mode === "new" &&
                dispatch(fetchTermAssociatedToBeneficiaries({ beneficiaryId: chosenBeneficiary?.id })).then((resp) => {
                    const beneficiaryTerms = resp.payload;
                    // If beneficiary already has a contract...
                    if (resp.meta.requestStatus === "fulfilled" && beneficiaryTerms.length > 0) {
                        if (!currentPeriod) return;

                        // Fetch payments info for current period:
                        const options = {
                            periodId: currentPeriod.id,
                            query: `${chosenBeneficiary.surname} ${chosenBeneficiary.name ?? ""}`,
                            paidStatus: "paid" as TFilterParamsPaymentStatus,
                        };
                        dispatch(fetchBeneficiariesInformation(options)).then((_resp) => {
                            if (_resp.meta.requestStatus === "fulfilled") {
                                const results = _resp.payload.entries;
                                const paymentExists =
                                    results.length > 0 && results.find((item) => item.id === chosenBeneficiary.id);
                                // If a paid payment exists for this beneficiary and they have selected a title:
                                const salesTitleId = chosenTitle?.id ?? chosenWork?.titles?.[0]?.id;
                                if (paymentExists && salesTitleId != null) {
                                    const dates = {
                                        startDate: currentPeriod.periodStart,
                                        endDate: currentPeriod.periodEnd,
                                    };
                                    dispatch(
                                        fetchTitleSales({
                                            titleId: salesTitleId,
                                            options: { dates },
                                        }),
                                    ).then((sales) => {
                                        if (sales.meta.requestStatus === "fulfilled") {
                                            const titleSales = sales.payload.entries;
                                            if (titleSales.length > 0)
                                                setOtherTermsMessage((prev) => ({ ...prev, payment: true }));
                                        }
                                    });
                                    if (+chosenAdvance > 0 || +chosenProvision > 0)
                                        setOtherTermsMessage((prev) => ({ ...prev, payment: true }));
                                }
                            }
                        });
                    }
                });
        } else {
            setOtherTermsMessage((prev) => ({ ...prev, payment: false, benefPeriods: false }));
        }
    }, [chosenBeneficiary, currentPeriod, chosenTitle, chosenWork?.id, workFirstTitleId]);

    // Triggers display of message if they select a period and benef or title have contracts on other active period.
    useEffect(() => {
        if (!currentPeriod || !periods || !usesMultiPeriods) return;
        const activePeriods = periods.filter((item) => item.isActive);

        // Once they've selected beneficiary and period, check if beneficiary has terms on other active period:
        // If termAssociatedBenef exists, it means they've selected a beneficiary (see prior useEffect)
        if (chosenPeriodType && termAssociatedToBeneficiaries.length) {
            const termInOtherPeriod = checkElementTerms({
                terms: termAssociatedToBeneficiaries,
                currentPeriodTypeId: currentPeriod.periodTypeId,
                activePeriods,
            });
            if (termInOtherPeriod.length) setOtherTermsMessage((prev) => ({ ...prev, benefPeriods: true }));
        }

        // Once title is selected, check if title has terms on other active period (no need to select period as this appears on the period field options).
        // If termAssociatedTitle exists, it means they've selected a title (see prior useEffect)
        const resourceTerms = isBookPublisher ? workTerms : termAssociatedToTitle;
        if (resourceTerms.length) {
            const termInOtherPeriod = checkElementTerms({
                terms: resourceTerms,
                currentPeriodTypeId: currentPeriod.periodTypeId,
                activePeriods,
            });

            if (termInOtherPeriod.length)
                setOtherTermsMessage((prev) => ({ ...prev, titlePeriods: termInOtherPeriod }));
        }
    }, [
        chosenPeriodType,
        termAssociatedToBeneficiaries,
        currentPeriod,
        termAssociatedToTitle,
        workTerms,
        isBookPublisher,
    ]);

    useEffect(() => {
        const linkedResource = isBookPublisher ? chosenWork : chosenTitle;
        const resourceTerms = isBookPublisher ? workTerms : termAssociatedToTitle;
        if (!linkedResource || !chosenBeneficiary || mode === "edit") return;

        const resourceTermsStatus = isBookPublisher ? workTermsStatus : termAssociatedToTitleStatus;
        if (resourceTermsStatus !== "succeeded") {
            setOtherTermsMessage((prev) => ({ ...prev, termExists: false }));
            return;
        }

        const similarTerms = resourceTerms.filter((item) => {
            const sameBeneficiary = item.beneficiarId === chosenBeneficiary.id;
            const representativeExists = !!currentRepresentative;
            const sameRepresentative =
                (item.representative ?? "").toLowerCase().replaceAll(" ", "") ===
                `${currentRepresentative?.surname} ${currentRepresentative?.name}`.toLowerCase().replaceAll(" ", "");

            // If they have chosen a representative, check that term exists with same benef AND benef.
            // If they haven't chosen a representative, check that term exists with same benef and no representative.
            const sameParty = representativeExists
                ? sameBeneficiary && sameRepresentative
                : sameBeneficiary && !item.representative;
            if (!sameParty) return false;
            if (item.hasEnded) return false;
            if (
                chosenPeriodType?.periodTypeId != null &&
                item.periodTypeId != null &&
                item.periodTypeId !== chosenPeriodType.periodTypeId
            ) {
                return false;
            }
            if (!isBookPublisher) return true;
            const incomingCoverage = normalizeCoveredTitleIds(
                (chosenCoveredTitles ?? []).map((title) => title.id),
                workTitlesForCoverage.map((title) => title.id),
            );
            return termCoveragesConflict(incomingCoverage, item.coveredTitleIds ?? []);
        });

        setOtherTermsMessage((prev) => ({ ...prev, termExists: !!similarTerms.length }));
    }, [
        chosenTitle,
        chosenWork,
        chosenBeneficiary,
        chosenCoveredTitles,
        workTitlesForCoverage,
        currentRepresentative,
        termAssociatedToTitle,
        termAssociatedToTitleStatus,
        workTerms,
        workTermsStatus,
        isBookPublisher,
        mode,
        chosenPeriodType,
    ]);

    useEffect(() => {
        onDirty(isDirty || haveChannelValuesChanged || hasAdvanceGroupsOptionChanged);
    }, [isDirty, haveChannelValuesChanged, hasAdvanceGroupsOptionChanged]);

    useEffect(() => {
        if (mode !== "edit") {
            setIsEditChannelsReady(true);
            return;
        }
        setIsEditChannelsReady(false);
    }, [mode, currentTerm?.termId]);

    useEffect(() => {
        // Parse data from an active channel, so I can use it in the Channels Step.
        if (mode === "edit" && currentTerm) {
            const parsedChannels = currentTerm.channelData?.map((channel) => {
                // Find the corresponding channel in channels to fall back to its default columns when the
                // contract has no stored value yet.
                const currentChannel = channels?.find((item) => item.id === channel.channelId);
                // Base and milestone columns are contract-scoped: milestone defaults to the base column.
                const calculationBaseColumn = currentChannel
                    ? resolveCalculationBaseColumn(currentChannel, channel.calculationBaseColumn)
                    : channel.calculationBaseColumn;
                const milestoneBaseColumn = resolveMilestoneBaseColumn(
                    channel.milestoneBaseColumn,
                    calculationBaseColumn ?? "quantity",
                );
                // Milestone thresholds are stored in the milestone column unit: monetary columns are in cents.
                const channelTypeMultiplier = milestoneBaseColumn === "quantity" ? 1 : 100;

                const groupId = channels.find((item) => item.id === channel.channelId)?.compensationGroupId;
                const storedMilestones = JSON.parse(channel.data) as ChannelData[];
                const { milestones, showDefaultConfiguration } = splitDefaultConfigurationFlag(storedMilestones);
                const newData = milestones.map((milestone) => ({
                    ...milestone,
                    quantity: +milestone.quantity / channelTypeMultiplier,
                }));
                groupId && setActiveGroups((prevGroups) => [...prevGroups, groupId]);
                return {
                    ...channel,
                    groupId: usesChannelCompensation ? groupId : -1,
                    data: newData,
                    calculationBaseColumn,
                    milestoneBaseColumn,
                    formatTypeId: channel.formatTypeId ?? null,
                    showDefaultConfiguration,
                };
            });

            const parsedChosenChannels = (parsedChannels ?? []) as ChannelParams[];
            setChosenChannels(parsedChosenChannels);
            if (groups) {
                setChosenGroups(
                    groups.map((group) => ({
                        ...group,
                        synced: areCompensationGroupFormulasSynced(
                            parsedChosenChannels.filter((channel) => channel.groupId === group.id),
                        ),
                    })),
                );
            }

            // Check if the advance is linked to a payment in Advance Management.
            if (advanceSlices.some((advance) => advance.termId === currentTerm.termId)) {
                setPaymentExistsInManagement(true);
            }

            setIsEditChannelsReady(true);
        }
    }, [currentTerm]);

    // In edit mode: fetch and pre-populate slices for the inline advance form
    useEffect(() => {
        if (mode !== "edit" || !linkedAdvancePayment) return;
        dispatch(
            fetchAdvancePaymentsOfTerm({
                termId: linkedAdvancePayment.termId,
                typeOfPayment: "advance",
                typeOfAdvance: "all",
            }),
        ).then(({ meta: { requestStatus }, payload }) => {
            if (requestStatus === "fulfilled" && payload?.data?.length) {
                // formatInitialSlices returns amounts in euros (÷100). Multiply back to cents
                // so advancePayment state always holds amounts in cents, consistent with
                // the InlineAdvanceForm sync effect which also writes cents.
                const advances = [...payload.data].reverse();
                const initialSlices = formatInitialSlices(advances).map((s) => ({
                    ...s,
                    amount: Math.round(+s.amount * 100),
                }));
                setAdvancePayment({
                    term: null,
                    slices: initialSlices,
                    isForfait: 0,
                    forceOver4Pass: 0,
                });
                setCreateAdvanceInManagement(true);
            }
        });
    }, [linkedAdvancePayment]);

    // If they edit channels from template or advancePayment we consider they're no longer using the template -> removes colour highlight.
    useEffect(() => {
        if (haveChannelValuesChanged || dirtyFields.advancePayment) {
            setUsesTemplate(false);
        }
    }, [haveChannelValuesChanged, dirtyFields]);

    // Needed to display option to inject data from templates:
    useEffect(() => {
        dispatch(fetchAllTermTemplates({ beneficiaryId: null }));
        if (mode === "new") {
            dispatch(resetTermAssociatedToTitles());
        }
    }, []);

    // Also fetch this beneficiary's framework contracts so they can be used as prefill.
    // Falls back to the URL/location beneficiary id for the edit flow.
    useEffect(() => {
        const beneficiaryId = chosenBeneficiary?.id ?? currentBeneficiaryId;
        if (beneficiaryId) {
            dispatch(fetchBeneficiaryFrameworkContracts({ beneficiaryId }));
        }
    }, [chosenBeneficiary?.id, currentBeneficiaryId]);

    const disableStep3Next = () => {
        const hasInvalidPositiveAdvance = usesInitialAdvance && chosenInitialAdvance > 0;
        return hasInvalidPositiveAdvance;
    };

    // Signal incremented each time the user clicks "Suivant" while the advance form is open
    // but has incomplete slices — triggers RHF validation inside InlineAdvanceForm
    const [advanceValidateSignal, setAdvanceValidateSignal] = useState(0);

    const wrappedTrigger: UseFormTrigger<ITermFormValues> = useCallback(
        async (...args) => {
            const pass = await trigger(...args);
            if (createAdvanceInManagement === false) {
                // Increment signal so InlineAdvanceForm triggers its own validation
                setAdvanceValidateSignal((prev) => prev + 1);
                return false;
            }
            return pass;
        },
        [trigger, createAdvanceInManagement],
    );

    const isDuplicateTermCheckPending =
        mode === "new" &&
        !!chosenBeneficiary &&
        (isBookPublisher
            ? !!chosenWork && workTermsStatus !== "succeeded"
            : !!chosenTitle && termAssociatedToTitleStatus !== "succeeded");

    const steps: IFormStep[] = [
        // Structure of steps is : title of the step, subtitle, its specific component (format) and if needed a condition to disable the Next button.
        {
            stepTitle: mode === "new" ? t("ConnectTitle.Step1Title") : t("ConnectTitle.Step1TitleBis"),
            stepSubtitle: isBookPublisher ? t("ConnectTitle.Step1TextWork") : t("ConnectTitle.Step1Text"),
            conditionToDisable: otherTermsMessage.termExists || isDuplicateTermCheckPending,
            format: (stepTitle: string, stepSubtitle: string) => (
                <NewTermStep
                    useFormData={{ control, errors, watch }}
                    currentTitleId={currentTitleId}
                    currentWorkId={currentWorkId}
                    isBookPublisher={isBookPublisher}
                    currentBeneficiaryId={currentBeneficiaryId}
                    associatedPersons={associatedPersons}
                    chosenBeneficiary={chosenBeneficiary}
                    chosenRepresentative={chosenRepresentative ?? currentRepresentative}
                    beneficiarsAutocomplete={beneficiarsAutocomplete}
                    indexStep={0}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    mode={mode}
                    triggerSaveButton={() => setHaveChannelValuesChanged(true)}
                    otherTermsMessage={otherTermsMessage}
                    setValue={setValue}
                    currentCoveredTitleIds={currentTerm?.coveredTitleIds}
                    workTitles={workTitlesForCoverage}
                    workTitlesLoading={workTitlesLoading}
                    coveredTitlesHydratedWorkIdRef={coveredTitlesHydratedWorkIdRef}
                />
            ),
        },
        {
            stepTitle: t("ConnectTitle.Step2Title"),
            stepSubtitle: t("ConnectTitle.Step2Text"),
            conditionToDisable:
                mode === "edit" ? !chosenChannels?.length : !activatedChannels.length || !!errors.channels,
            format: (stepTitle: string, stepSubtitle: string) => (
                <ChannelsStep
                    from="form"
                    indexStep={1}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    channels={channels}
                    filteredChannels={filteredChannels}
                    activedChannels={activedChannels}
                    chosenChannels={chosenChannels}
                    setChosenChannels={setChosenChannels}
                    onAddChannel={setAddChannel}
                    activatedChannels={activatedChannels}
                    setActivatedChannels={setActivatedChannels}
                    mode={mode}
                    onChange={() => setHaveChannelValuesChanged(true)}
                    usesCompensation={usesChannelCompensation}
                    groupsWithChannels={groupsWithChannels}
                    activeGroups={activeGroups}
                    setActiveGroups={setActiveGroups}
                    chosenGroups={chosenGroups}
                    setChosenGroups={setChosenGroups}
                    channelCompensationGroups={channelCompensationGroups}
                    usesTemplate={usesTemplate}
                    setUsesTemplate={setUsesTemplate}
                    termTemplates={termTemplates}
                    formatTypesOnWork={companyFormatTypes}
                    frameworkContracts={beneficiaryFrameworkContracts}
                    useFormData={{ setError, clearErrors, setValue }}
                    isEditChannelsReady={isEditChannelsReady}
                />
            ),
        },
        {
            stepTitle: t("ConnectTitle.Step3Title"),
            stepSubtitle: t("ConnectTitle.Step3Text"),
            conditionToDisable: disableStep3Next(),
            format: (stepTitle: string, stepSubtitle: string) => (
                <SettingsStep
                    useFormData={{ control, errors, setValue, clearErrors, dirtyFields, watch }}
                    formData={{
                        chosenAdvance,
                        chosenAdvanceGroup,
                        chosenBeneficiary: chosenBeneficiary && chosenBeneficiary,
                        chosenInitialAdvance,
                        chosenCumulativeRights,
                    }}
                    indexStep={2}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    termGroups={termGroups}
                    mode={mode}
                    linkedAdvancePayment={linkedAdvancePayment}
                    advancePayment={advancePayment}
                    setStateFunctions={{
                        setTermHasAdvanceGroups,
                        setCreateAdvanceInManagement,
                        setHasAdvanceGroupsOptionChanged,
                        setAdvancePayment,
                    }}
                    booleanValues={{
                        usesTemplate,
                        termHasAdvanceGroups,
                        createAdvanceInManagement,
                        usesAdvanceCompensation,
                        termExistOnClosePeriod: !!currentTerm?.termExistsOnClosedPeriod,
                        termHasEnded: !!currentTerm?.hasEnded,
                        usesInitialAdvance,
                        usesCumulativeRoyalties,
                        advanceValidateSignal,
                    }}
                    currentPeriod={currentPeriod}
                    titleEan13={chosenTitle?.ean13 ?? chosenWork?.titles?.[0]?.ean13}
                />
            ),
        },
        {
            stepTitle: t("ConnectTitle.Step4Title"),
            stepSubtitle: t("ConnectTitle.Step4Text"),
            format: (stepTitle: string, stepSubtitle: string) => (
                <RecapStep
                    indexStep={3}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    getValues={getValues()}
                    useFormData={{ watch: watch }}
                    channels={channels}
                    chosenChannels={chosenChannels}
                    chosenCompensation={chosenCompensation}
                    channelGroups={channelCompensationGroups ?? []}
                    groupsWithChannels={groupsWithChannels}
                    usesCompensation={usesChannelCompensation}
                    chosenGroups={chosenGroups}
                    usesAdvanceCompensation={usesAdvanceCompensation}
                    chosenAdvanceGroup={chosenAdvanceGroup}
                    createAdvanceInManagement={createAdvanceInManagement}
                    advancePaymentExists={paymentExistsInManagement}
                    otherTermsMessage={otherTermsMessage}
                    usesInitialAdvance={usesInitialAdvance}
                    usesCumulativeRoyalties={usesCumulativeRoyalties}
                    inlineAdvancePayment={advancePayment}
                    mode={mode}
                    currentPeriod={currentPeriod}
                />
            ),
        },
    ];

    const dataForSteps = [
        // Strings that appear when a step is passed.
        `${chosenWork?.name ?? chosenTitle?.name} <> ${chosenBeneficiary?.name} ${chosenBeneficiary?.surname}`,
        `${chosenChannels?.length ?? 0} ${
            chosenChannels?.length === 1 ? t("ConnectTitle.OneActiveChannel") : t("ConnectTitle.ActiveChannels")
        }`,
    ];

    const deleteStep = {
        // Text specific to the Archiving step.
        title: t("ConnectTitle.DeleteStepTitle"),
        subtitle: t("ConnectTitle.DeleteStepSubtitle"),
    };

    return (
        <div className="new-term-container" style={{ background: `${mode === "edit" ? "transparent" : ""}` }}>
            <ModalBase
                open={addRepresentative}
                onClose={() => setAddRepresentative(false)}
                modalTitle={t("Representatives.Form.TitleNew")}
                modalSubtitle={t("Representatives.Form.Subtitle")}
                widthTransition={{ active: true, width: "590px" }}
            >
                <RepresentativeForm
                    beneficiary={chosenBeneficiary}
                    formMode={"new"}
                    onClose={handleCreateRepresentative}
                />
            </ModalBase>
            <CreateChannelModal mode="new" openModal={addChannel} closeModal={() => setAddChannel(false)} />

            {/* This is the component that can be reused for every form. */}
            <FormProvider {...formMethods}>
                <NewForm
                    useFormData={{
                        trigger: wrappedTrigger,
                        handleSubmit,
                        isDirty,
                        dirtyFields,
                    }}
                    onSubmit={onSubmit}
                    txtBtnSubmitNew={t("ConnectTitle.CreateContract")}
                    dataForSteps={dataForSteps}
                    steps={steps}
                    deleteStep={deleteStep}
                    mode={mode}
                    onDelete={() => currentTerm && onDelete && onDelete(currentTerm?.termId as number)}
                    haveInDepthValuesChanged={
                        haveChannelValuesChanged ||
                        hasAdvanceGroupsOptionChanged ||
                        // In edit mode, advancePayment is loaded from the API on mount — not a user change.
                        // Advance payments are read-only in edit mode so this never changes.
                        (mode === "new" && !!advancePayment)
                    }
                    // There is the "to" section and the actual name. Both need to have fallback, otherwise "to" stays
                    returnTo={
                        currentBeneficiaryId
                            ? `${chosenBeneficiary?.name ? t("ConnectTitle.To1") : ""} ${
                                  chosenBeneficiary?.name ?? ""
                              } ${chosenBeneficiary?.surname ?? ""}`
                            : `${chosenWork?.name ?? chosenTitle?.name ? t("ConnectTitle.To1") : ""} ${
                                  chosenWork?.name ?? chosenTitle?.name ?? ""
                              }`
                    }
                    submitFormStatus={submitFormStatus}
                    setOtherTermsMessage={setOtherTermsMessage}
                    displaysWarning
                    infoLink={{
                        type: t("ConnectTitle.Term").toLowerCase(),
                        link: "https://helpdesk.crealo.app/fr/article/creer-un-nouveau-contrat-1hcmswb/",
                    }}
                    handleScrollToErrors={() => handleScrollToErrors(errors)}
                />
            </FormProvider>
        </div>
    );
};

export default TermFormContainer;
